import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { deleteRequest, getRequest, postRequest } from '../../../services/base'
import Button from '../../common/Button'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { NavContext } from '../../../context/global_context'
import TracerMemos from './TracerMemos'
import {
  MemoContext,
  SELECT_DTC_MEMO,
  SET_DTC_MEMO_LIST,
  SET_MEMOS_DATE,
  SHOW_FETCH_MEMO_POPUP,
} from './context/context'
import InputText from '../../common/InputText'
import DtcMemoFormPopup from './form'
import { getSessionStorageData } from '../../../utils/SessionStorage'
import { Empty, Flex, Form, Modal, Row, Segmented, Space, Table } from 'antd'
import useDebouncedValue from '../../../utils/common_function'
import DateTimeInput from '../../common/DateTimeInput'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import MemoTable from './MemoTable'
import { useIntl } from 'react-intl'
import { notifyError } from '../../../utils/notifications'

dayjs.extend(customParseFormat)

const Memos = () => {
  let { vehicle } = useParams()
  const intl = useIntl()
  const [state, dispatch] = useContext(MemoContext)
  const [navState, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef(
    `/api/dtc_memos/?vehicle=${vehicle}&search=${search}`
  )

  const location = useLocation()
  const navigate = useNavigate()
  const debouncedSearch = useDebouncedValue(search, 500)
  const [modal, contextHolder] = Modal.useModal()
  const menuItems = [
    {
      label: intl.formatMessage({ id: 'sidebar.taMappings' }),
      value: `/vehicles/${vehicle}/dtcs/listing`,
    },
    {
      label: intl.formatMessage({ id: 'sidebar.csMappings' }),
      value: `/vehicles/${vehicle}/dtcs/tracer_mapping`,
    },
    {
      label: intl.formatMessage({ id: 'sidebar.csMemos' }),
      value: `/vehicles/${vehicle}/dtcs/memos`,
    },
  ]

  useEffect(() => {
    getDtcMemos()
  }, [])

  const getDtcMemos = async (url) => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header: intl.formatMessage({ id: 'layout.vehicleList' }) + ` / ${vehicle} / DTCs / ` + intl.formatMessage({ id: 'sidebar.csMemos' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: getSessionStorageData('vehicle')?.search
          ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
          : `/vehicles`,
      },
    })
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'memos.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_DTC_MEMO_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: {
        caption: '',
        showLoader: false,
      },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'memos.loadError' }))
    }
  }

  const buildDtcMemoUrl = ({ vehicle, search, limit, offset }) => {
    const params = new URLSearchParams({
      vehicle,
      search,
      limit,
      offset,
    })

    return `/api/dtc_memos/?${params.toString()}`
  }

  const deleteDtcMemos = async (memo_id) => {
    let res = await deleteRequest(
      `/api/dtc_memos/${memo_id}/?vehicle=${vehicle}`
    )
    // Handling edge case of deleting the n..th single last record listed on the last page
    const { currentPage, totalCount, totalPages, nextPageLink, prevPageLink } = pagination
    const pageSize = 10
    const newTotalCount = totalCount - 1
    const newTotalPages = Math.max(1, Math.ceil(newTotalCount / pageSize))
    const pageToFetch = Math.min(currentPage, newTotalPages)
    const offset = (pageToFetch - 1) * pageSize

    getDtcMemos(buildDtcMemoUrl({
      vehicle: vehicle,
      search: search,
      limit: pageSize,
      offset: offset,
    }))
    navDispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: intl.formatMessage({ id: 'memos.deleteSuccess' }),
      },
    })
  }

  useEffect(() => {
    currentUrl.current = `/api/dtc_memos/?vehicle=${vehicle}&search=${search}`
    getDtcMemos()
  }, [debouncedSearch])

  const confirmImportAllFromTracer = () => {
    const title = intl.formatMessage({ id: 'memos.confirmImport' })
    const content = intl.formatMessage({ id: 'memos.confirmImportContent' })

    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk: () => {
        dispatch({ type: SHOW_FETCH_MEMO_POPUP, data: true })
      },
      centered: true,
      maskClosable: true,
    })
  }

  const confirmDeleteMemo = (memo_id) => {
    const title = intl.formatMessage({ id: 'memos.confirmDelete' })
    const content = intl.formatMessage({ id: 'memos.confirmDeleteContent' })

    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk: () => {
        deleteDtcMemos(memo_id)
      },
      centered: true,
      maskClosable: true,
    })
  }

  const dataSource = useMemo(() => {
    if(!state?.memo_list?.length) return []
    
    return state?.memo_list?.map((memo, index) => {
      return {
        ...memo,
        rowKey: `memo-${memo.id}-${memo.dtc_obj?.name ?? memo.dtc}-${memo?.acronym}`,
        key: `memo-${index}`,
        dtc_name: memo.dtc_obj?.name ?? memo.dtc,
        index: index,
      }
    })
  }, [state.memo_list])

  return (
    <>
      {contextHolder}
      
      <div className="page tracer-memos">
        <Row className="title">
          <Segmented
            options={menuItems}
            value={location.pathname}
            onChange={(value) => navigate(value)}
          />
        </Row>

        <Row className="body">
          <Form className="full-width">
            <Flex justify="space-between" className="full-width" gap={16}>
              <InputText
                name="search_dtcs_memo"
                type="search"
                placeholder={intl.formatMessage({ id: 'memos.searchPlaceholder' })}
                value={search}
                isRequired={false}
                wrapperClass="search-dtc-memo"
                onChange={(value) => {
                  setSearch(value)
                }}
              />

              <Space size={'middle'} align="center">
                <DateTimeInput
                  name={`memo_fetch_Date`}
                  value={state?.memos_date}
                  placeholder={intl.formatMessage({ id: 'memos.selectDatePlaceholder' })}
                  showTime={{
                    defaultOpenValue: dayjs('00:00', 'HH:mm'),
                    format: 'HH:mm',
                  }}
                  needConfirm={false}
                  isRequired={false}
                  wrapperClass="date-time"
                  onChange={(value) => {
                    dispatch({ type: SET_MEMOS_DATE, data: value })
                  }}
                />

                <Button
                  onClick={() => {
                    confirmImportAllFromTracer()
                  }}
                >
                  {intl.formatMessage({ id: 'memos.importAllFromTracer' })}
                </Button>

                <Button
                  type='primary'
                  onClick={(e) => {
                    dispatch({
                      type: SELECT_DTC_MEMO,
                      data: { selected_memo: {}, show_memo_popup: true },
                    })
                  }}
                >
                  {intl.formatMessage({ id: 'memos.createNewMemo' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex>
            <MemoTable
              dataSource={dataSource}
              showAction={false}
              pagination={pagination}
              getDtcMemos={getDtcMemos}
              onDelete={confirmDeleteMemo}
            />
          </Flex>
        </Row>
      </div>

      {/* Crowdsourced Memos Popup */}
      <TracerMemos
        getListData={() => {
          getDtcMemos()
        }}
      />

      {/* Create / Edit Memo Popup */}
      {state.show_memo_popup && (
        <DtcMemoFormPopup
          getListData={() => {
            getDtcMemos()
          }}
        />
      )}
    </>
  )
}

export default Memos
